import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { DayPicker, Matcher } from "react-day-picker";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

type DatePickerFieldProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabledSelectDate?: Matcher | Matcher[];
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  name?: string;
  showClear?: boolean; // show an inline clear icon to reset value
};

/**
 * A lightweight date picker field styled to match SelectField.
 * It wraps a native input[type="date"] for broad browser support and simplicity.
 */
const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabledSelectDate,
  disabled,
  errorMessage,
  className,
  name,
  showClear,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const displayValue = useMemo(() => {
    if (!value) return "";
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const d = new Date(value);
      return isValid(d) ? format(d, "yyyy-MM-dd") : "";
    } catch {
      return "";
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getDropdownStyle = () => {
    if (!containerRef.current || !isOpen) return {} as React.CSSProperties;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const dropdownHeight = 356;
    const margin = 12;
    let left = rect.left;
    let top = rect.bottom + 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + dropdownWidth + margin > vw) left = vw - dropdownWidth - margin;
    if (top + dropdownHeight + margin > vh) top = rect.top - dropdownHeight - 8;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    return { position: "fixed" as const, left, top, zIndex: 1000 };
  };

  const handleSelect = (d?: Date) => {
    if (!d || disabled) return;
    try {
      setIsOpen(false);
      const formatted = format(d, "yyyy-MM-dd");
      if (onChange) onChange(formatted);
    } catch {}
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (onChange) onChange("");
  };

  return (
    <div className={`w-full ${className || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      ) : null}
      <div ref={containerRef} className="relative">
        <input
          type="text"
          name={name}
          value={displayValue}
          onChange={() => {}}
          disabled={disabled}
          placeholder={placeholder || t("Select date")}
          className="w-full pr-10 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400"
          onClick={() => !disabled && setIsOpen((o) => !o)}
          readOnly
        />
        {showClear && !!displayValue && !disabled ? (
          <button
            type="button"
            aria-label={t("Clear date")}
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XIcon className="w-4 h-4" />
          </button>
        ) : (
          <CalendarIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        )}
        {isOpen && (
          <div
            ref={dropdownRef}
            style={getDropdownStyle()}
            className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <DayPicker
              mode="single"
              selected={displayValue ? parseISO(displayValue) : undefined}
              onSelect={handleSelect}
              showOutsideDays
              fixedWeeks
              disabled={disabledSelectDate}
              className="p-2"
            />
          </div>
        )}
      </div>
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
};

export default DatePickerField;
